import { useState, useEffect, useCallback, useMemo } from 'react';
import { useQuery } from 'react-query';
import API from '../../../API';

/**
 * Deal Flow Connection Manager
 * Manages the connection between activity feed and creator selection
 * Provides unified state management for the deal flow interface
 */

export const useDealFlowConnection = () => {
  // Core state
  const [selectedCreator, setSelectedCreator] = useState(null);
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [feedPage, setFeedPage] = useState(0);
  const [activityFilter, setActivityFilter] = useState('all'); // 'all', 'Campaign', 'Partnership', 'Payout'
  
  // Activity feed data
  const { 
    data: feedData, 
    isLoading: feedLoading, 
    refetch: refetchFeed 
  } = useQuery({
    queryKey: ['agency', 'zapfeed', { page: feedPage, filter: activityFilter }],
    queryFn: () => API.agency.getZapfeed({ 
      rows: 10, 
      page: feedPage,
      ...(activityFilter !== 'all' && { activity_type: activityFilter })
    }),
    refetchOnWindowFocus: false,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  // Creators list data
  const { 
    data: creators, 
    isLoading: creatorsLoading 
  } = useQuery({
    queryKey: ['agency', 'creators'],
    queryFn: () => API.creators.list({ only_agency: true }),
    refetchOnWindowFocus: false,
  });

  // Selected creator details
  const { 
    data: creatorDetails, 
    isLoading: creatorDetailsLoading 
  } = useQuery({
    queryKey: ['creator', 'details', selectedCreator],
    queryFn: () => API.creators.fetchDetails(selectedCreator),
    enabled: !!selectedCreator,
    refetchOnWindowFocus: false,
  });

  // Selected creator full data (campaigns, partnerships, payouts)
  const { 
    data: creatorFullData, 
    isLoading: creatorFullDataLoading 
  } = useQuery({
    queryKey: ['agency', 'creator', selectedCreator],
    queryFn: () => API.agency.getCreatorById(selectedCreator),
    enabled: !!selectedCreator,
    refetchOnWindowFocus: false,
  });

  // Activity details transformation
  const getActivityDetails = useCallback((activity) => {
    const price = activity.rate * (1 - (activity.fee_percentage / 100));
    const baseDetails = {
      id: `${activity.activity_type}-${activity.activity_id}`,
      creator: activity.creator_id,
      price,
      date: new Date().toLocaleDateString(),
      avatar: activity.profile_picture,
      rawActivity: activity,
    };

    switch (activity.activity_type) {
      case 'Campaign':
        return {
          ...baseDetails,
          title: `Campaign Collaboration`,
          subtitle: activity.activity_name,
          description: `New campaign partnership with ${activity.creator_id}`,
          type: 'Campaign',
          priority: 'high',
          status: 'active',
          link: `/agency/creators/${activity.creator_id}/collaborations/campaigns/${activity.activity_id}`,
        };
      case 'Partnership':
        return {
          ...baseDetails,
          title: `Partnership Deal`,
          subtitle: activity.activity_name,
          description: `Brand partnership agreement with ${activity.creator_id}`,
          type: 'Partnership',
          priority: 'medium',
          status: 'pending',
          link: `/agency/creators/${activity.creator_id}/collaborations/partnerships/${activity.activity_id}`,
        };
      case 'Payout':
        return {
          ...baseDetails,
          title: `Payment Processed`,
          subtitle: activity.activity_name,
          description: `Payout completed for ${activity.creator_id}`,
          type: 'Payout',
          priority: 'low',
          status: 'completed',
          link: `/agency/creators/${activity.creator_id}/payouts`,
        };
      default:
        return {
          ...baseDetails,
          title: 'Unknown Activity',
          subtitle: 'N/A',
          description: 'Unknown activity type',
          type: 'Unknown',
          priority: 'low',
          status: 'unknown',
          link: '',
        };
    }
  }, []);

  // Processed activities with enhanced data
  const processedActivities = useMemo(() => {
    if (!feedData?.zapfeed) return [];
    return feedData.zapfeed.map(getActivityDetails);
  }, [feedData, getActivityDetails]);

  // Filtered creators based on search
  const filteredCreators = useMemo(() => {
    if (!creators) return [];
    return creators.filter(creator =>
      searchQuery === '' ||
      creator.creator.toLowerCase().includes(searchQuery.toLowerCase()) ||
      creator.display_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      creator.notes_content_style?.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [creators, searchQuery]);

  // Creator statistics
  const creatorStats = useMemo(() => {
    if (!creators) return { total: 0, active: 0, topPerformers: [] };
    
    const total = creators.length;
    const active = creators.filter(c => c.status !== 'inactive').length;
    
    // Calculate top performers based on total reach
    const topPerformers = creators
      .map(creator => ({
        ...creator,
        totalReach: (parseInt(creator.youtube) || 0) + 
                   (parseInt(creator.instagram) || 0) + 
                   (parseInt(creator.tiktok) || 0)
      }))
      .sort((a, b) => b.totalReach - a.totalReach)
      .slice(0, 5);

    return { total, active, topPerformers };
  }, [creators]);

  // Activity statistics
  const activityStats = useMemo(() => {
    if (!processedActivities.length) return {
      total: 0,
      campaigns: 0,
      partnerships: 0,
      payouts: 0,
      totalRevenue: 0,
      avgDealSize: 0
    };

    const total = processedActivities.length;
    const campaigns = processedActivities.filter(a => a.type === 'Campaign').length;
    const partnerships = processedActivities.filter(a => a.type === 'Partnership').length;
    const payouts = processedActivities.filter(a => a.type === 'Payout').length;
    const totalRevenue = processedActivities.reduce((sum, a) => sum + a.price, 0);
    const avgDealSize = totalRevenue / total || 0;

    return { total, campaigns, partnerships, payouts, totalRevenue, avgDealSize };
  }, [processedActivities]);

  // Get creator collaborations (campaigns + partnerships)
  const getCreatorCollaborations = useCallback(() => {
    if (!creatorFullData) return { campaigns: [], partnerships: [], all: [] };

    const campaigns = (creatorFullData.campaigns || [])
      .filter((c) => ["Launched", "Completed"].includes(c.campaign_status))
      .map((c) => {
        const matchingCreator = c.creators?.find(
          (cr) => cr.id === selectedCreator
        );
        return {
          id: c.id,
          name: c.name,
          type: 'campaign',
          status: matchingCreator?.status || '',
          campaignStatus: c.campaign_status,
          dueDate: c.ideal_duedate,
          price: c.price || 0,
          link: `/agency/creators/${selectedCreator}/collaborations/campaigns/${c.id}`,
        };
      });

    const partnerships = (creatorFullData.partnerships || []).map((p) => ({
      id: p.id,
      name: p.name,
      type: 'partnership',
      status: p.status || '',
      dueDate: p.finish_date,
      price: p.total_sum || 0,
      link: `/agency/creators/${selectedCreator}/collaborations/partnerships/${p.id}`,
    }));

    return {
      campaigns,
      partnerships,
      all: [...campaigns, ...partnerships]
    };
  }, [creatorFullData, selectedCreator]);

  // Get creator payouts
  const getCreatorPayouts = useCallback(() => {
    if (!creatorFullData?.payouts) return [];
    
    return creatorFullData.payouts.map(payout => ({
      id: payout.id,
      amount: parseFloat(payout.amount) || 0,
      status: payout.status?.toLowerCase() || 'pending',
      payoutDate: payout.payout_date,
      poNumber: payout.po_number,
      campaignName: payout.campaign_name,
      interestRate: payout.interest_rate || 0,
      role: payout.role,
      blitzpay: payout.blitzpay,
      notes: payout.notes,
    }));
  }, [creatorFullData]);

  // Handle activity selection with automatic creator selection
  const handleActivitySelect = useCallback((activity) => {
    const activityDetails = getActivityDetails(activity);
    setSelectedActivity(activityDetails);
    setSelectedCreator(activityDetails.creator);
  }, [getActivityDetails]);

  // Handle creator selection (clears activity selection)
  const handleCreatorSelect = useCallback((creatorId) => {
    setSelectedCreator(creatorId);
    setSelectedActivity(null);
  }, []);

  // Clear all selections
  const clearSelections = useCallback(() => {
    setSelectedCreator(null);
    setSelectedActivity(null);
  }, []);

  // Find activities for a specific creator
  const getCreatorActivities = useCallback((creatorId) => {
    return processedActivities.filter(activity => activity.creator === creatorId);
  }, [processedActivities]);

  // Get creator performance metrics
  const getCreatorMetrics = useCallback((creatorId) => {
    if (!creatorDetails?.creator) return null;

    const creator = creatorDetails.creator;
    const totalReach = (parseInt(creator.youtube) || 0) + 
                      (parseInt(creator.instagram) || 0) + 
                      (parseInt(creator.tiktok) || 0);
    
    const creatorActivities = getCreatorActivities(creatorId);
    const totalRevenue = creatorActivities.reduce((sum, a) => sum + a.price, 0);
    const activeDeals = creatorActivities.filter(a => a.status === 'active').length;

    return {
      totalReach,
      totalRevenue,
      activeDeals,
      platforms: {
        youtube: parseInt(creator.youtube) || 0,
        instagram: parseInt(creator.instagram) || 0,
        tiktok: parseInt(creator.tiktok) || 0,
      },
      engagementScore: Math.min(100, Math.round((totalReach / 1000000) * 15 + creatorActivities.length * 10)),
    };
  }, [creatorDetails, getCreatorActivities]);

  // Pagination handlers
  const nextPage = useCallback(() => {
    const totalPages = Math.ceil((feedData?.total || 0) / 10);
    if (feedPage < totalPages - 1) {
      setFeedPage(prev => prev + 1);
    }
  }, [feedData, feedPage]);

  const prevPage = useCallback(() => {
    if (feedPage > 0) {
      setFeedPage(prev => prev - 1);
    }
  }, [feedPage]);

  // Search and filter handlers
  const updateSearch = useCallback((query) => {
    setSearchQuery(query);
  }, []);

  const updateActivityFilter = useCallback((filter) => {
    setActivityFilter(filter);
    setFeedPage(0); // Reset to first page when filtering
  }, []);

  // Auto-refresh feed
  useEffect(() => {
    const interval = setInterval(() => {
      refetchFeed();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [refetchFeed]);

  return {
    // State
    selectedCreator,
    selectedActivity,
    searchQuery,
    feedPage,
    activityFilter,

    // Data
    activities: processedActivities,
    creators: filteredCreators,
    creatorDetails: creatorDetails?.creator,
    creatorFullData,
    
    // Loading states
    feedLoading,
    creatorsLoading,
    creatorDetailsLoading,
    creatorFullDataLoading,

    // Statistics
    creatorStats,
    activityStats,

    // Actions
    handleActivitySelect,
    handleCreatorSelect,
    clearSelections,
    updateSearch,
    updateActivityFilter,
    nextPage,
    prevPage,

    // Utilities
    getCreatorActivities,
    getCreatorMetrics,
    getActivityDetails,
    getCreatorCollaborations,
    getCreatorPayouts,

    // Raw data access
    rawFeedData: feedData,
    rawCreators: creators,
  };
};

/**
 * Activity type configuration for UI rendering
 */
export const ACTIVITY_TYPES = {
  Campaign: {
    label: 'Campaign',
    color: '#f093fb',
    icon: 'Campaign',
    priority: 'high',
    description: 'Brand campaign collaborations'
  },
  Partnership: {
    label: 'Partnership',
    color: '#4facfe',
    icon: 'Handshake',
    priority: 'medium',
    description: 'Long-term brand partnerships'
  },
  Payout: {
    label: 'Payout',
    color: '#43e97b',
    icon: 'AccountBalance',
    priority: 'low',
    description: 'Payment transactions'
  }
};

/**
 * Priority levels for activity sorting and display
 */
export const PRIORITY_LEVELS = {
  high: { label: 'High', color: '#ff6b6b', weight: 3 },
  medium: { label: 'Medium', color: '#ffa726', weight: 2 },
  low: { label: 'Low', color: '#66bb6a', weight: 1 }
};

/**
 * Status configurations for activities
 */
export const STATUS_TYPES = {
  active: { label: 'Active', color: '#43e97b' },
  pending: { label: 'Pending', color: '#ffa726' },
  completed: { label: 'Completed', color: '#66bb6a' },
  cancelled: { label: 'Cancelled', color: '#ef5350' }
};

export default useDealFlowConnection;